import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { agentDisplay } from '../agents';
import { BRAND_META } from '../brand';
import { ApiError, type ApiClient } from '../api';
import { timeAgo } from '../format';
import type { ApprovalItem, ApprovalsResponse, BrandName, Me } from '../types';

/**
 * The brand a queued action belongs to, from its payload.
 *
 * The brand was only ever inside the summary sentence ("Schedule
 * equalishop post for..."), which is the one thing a reviewer needs to
 * see before reading anything else: the rules that make an item wrong
 * are brand rules. Uses the Shell's existing per-brand hue so it agrees
 * with the map, the tiles and the feed.
 */
function BrandChip({ payload }: { payload: Record<string, unknown> }) {
  const raw = payload.brand;
  if (typeof raw !== 'string') return null;
  const meta = BRAND_META[raw as BrandName] as (typeof BRAND_META)[BrandName] | undefined;
  const market = typeof payload.market === 'string' ? payload.market.toUpperCase() : null;
  return (
    <span className="brand-chip" style={{ ['--brand-color' as string]: meta?.series ?? 'var(--ink-3)' }}>
      {meta?.label ?? raw}
      {market && <span className="brand-chip-market">{market}</span>}
    </span>
  );
}

/** §14.6: consistent colour and icon per agent, shared with Slack. */
function AgentChip({ agent }: { agent: string }) {
  const display = agentDisplay(agent);
  return (
    <span className="agent-chip" style={{ ['--agent-color' as string]: display.color }}>
      <span aria-hidden>{display.icon}</span> {display.displayName}
    </span>
  );
}

/**
 * The approval queue (§8): one list across all agents, sortable by age and
 * agent; each item shows the agent, the one-line summary, the payload
 * rendered readably and the agent's own rationale; three actions with a
 * mandatory rejection reason; keyboard-first on desktop and thumb-usable
 * on mobile. Clearing this list is the daily ritual the whole system
 * depends on, so speed of clearing beats density of chrome.
 */

type Mode =
  | { kind: 'view' }
  | { kind: 'reject'; reason: string }
  | { kind: 'edit'; text: string; error: string | null }
  // 'when' is the scheduled-or-now choice. `at` is an ISO instant, not the
  // input's local string, so the value crossing the wire is unambiguous
  // wherever the approver happens to be.
  | { kind: 'when'; at: string; confirming: boolean };

/**
 * The scheduled time on an approval, or null when the tool has none.
 * schedule_post requires scheduled_for; publish_post has no such field and
 * goes out immediately, so it offers no choice to make.
 *
 * An 'edited' decision means the EDITED payload is what ran - that is what
 * the executor reads, and moving this very field is the whole point of the
 * edit. Reading item.payload here told an approver who had just pressed
 * Post now that their post goes out next Saturday, which is the original
 * time they had replaced. The post was fine; the label was lying.
 */
function effectivePayload(item: {
  decision?: string | null;
  payload?: Record<string, unknown> | null;
  edited_payload?: Record<string, unknown> | null;
}): Record<string, unknown> | null | undefined {
  return item.decision === 'edited' && item.edited_payload ? item.edited_payload : item.payload;
}

function scheduledFor(item: {
  decision?: string | null;
  payload?: Record<string, unknown> | null;
  edited_payload?: Record<string, unknown> | null;
}): string | null {
  const value = effectivePayload(item)?.scheduled_for;
  return typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : null;
}

/**
 * The scheduled time as a person reads it, in their own timezone and
 * naming the day. "2026-09-14T10:00:00Z" tells an approver nothing at a
 * glance; "Mon 14 Sep, 11:00" tells them whether it is soon.
 */
function whenLabel(iso: string): string {
  // Europe/London EXPLICITLY, not the device timezone (16 September
  // 2026: a laptop sitting on UTC+2 rendered a 09:00Z send as 11:00 -
  // same family as the brief header reading yesterday). Every schedule
  // in this platform is stated in Europe/London; the Shell reads them
  // back in the same clock, wherever the reader happens to be.
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/London',
  });
}

/** ISO instant -> the value a datetime-local input wants (local, no zone). */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** ...and back. An unparseable input keeps the clock where it was. */
function fromLocalInput(local: string): string {
  const at = new Date(local);
  return Number.isNaN(at.getTime()) ? new Date().toISOString() : at.toISOString();
}

export function ApprovalsView({
  api,
  me,
  initialSelectedId,
}: {
  api: ApiClient;
  me: Me;
  initialSelectedId?: string | null;
}) {
  const [tab, setTab] = useState<'pending' | 'decided'>('pending');
  const [sort, setSort] = useState<'age' | 'agent'>('age');
  const [items, setItems] = useState<ApprovalItem[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId ?? null);
  const [mode, setMode] = useState<Mode>({ kind: 'view' });
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const res = await api.get<ApprovalsResponse>(`/api/approvals?state=${tab}`);
    setItems(res.approvals);
  }, [api, tab]);

  useEffect(() => {
    setItems(null);
    setMode({ kind: 'view' });
    void load();
    const id = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(id);
  }, [load]);

  const sorted = useMemo(() => {
    if (!items) return null;
    return sort === 'age'
      ? items // server order: oldest first for pending, newest first for decided
      : [...items].sort((a, b) => a.agent.localeCompare(b.agent) || a.requested_at.localeCompare(b.requested_at));
  }, [items, sort]);

  const selected = sorted?.find((i) => i.id === selectedId) ?? sorted?.[0] ?? null;

  const move = useCallback(
    (delta: number) => {
      if (!sorted || sorted.length === 0) return;
      const idx = Math.max(0, sorted.findIndex((i) => i.id === selected?.id));
      const next = sorted[Math.min(sorted.length - 1, Math.max(0, idx + delta))];
      if (next) setSelectedId(next.id);
    },
    [sorted, selected],
  );

  const decide = useCallback(
    async (item: ApprovalItem, body: Record<string, unknown>) => {
      setBusy(true);
      setNotice(null);
      try {
        await api.post(`/api/approvals/${item.id}/decision`, body);
        setMode({ kind: 'view' });
        await load();
        setSelectedId(null); // fall to the next oldest: keep clearing
      } catch (err) {
        if (err instanceof ApiError && err.code === 'step_up_required') {
          setNotice(
            `Deciding ${item.action_class} actions needs a fresh sign-in. Sign out and back in, then decide it.`,
          );
        } else if (err instanceof ApiError && (err.code === 'already_decided' || err.code === 'expired')) {
          setNotice(err.code === 'expired' ? 'That item expired and was dropped.' : 'Someone else decided that one first.');
          await load();
        } else {
          setNotice(`The decision did not save: ${(err as Error).message}`);
        }
      } finally {
        setBusy(false);
      }
    },
    [api, load],
  );

  // Keyboard-first (§8): j/k move, a approve, r reject, e edit-and-approve.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (tab !== 'pending' || !selected) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
        if (e.key === 'Escape') setMode({ kind: 'view' });
        return;
      }
      if (mode.kind !== 'view') {
        if (e.key === 'Escape') setMode({ kind: 'view' });
        return;
      }
      if (e.key === 'j' || e.key === 'ArrowDown') move(1);
      else if (e.key === 'k' || e.key === 'ArrowUp') move(-1);
      else if (e.key === 'a' && !busy) void decide(selected, { decision: 'approved' });
      else if (e.key === 'r') setMode({ kind: 'reject', reason: '' });
      else if (e.key === 't' && scheduledFor(selected) !== null)
        setMode({ kind: 'when', at: scheduledFor(selected)!, confirming: false });
      else if (e.key === 'e')
        setMode({ kind: 'edit', text: JSON.stringify(selected.payload, null, 2), error: null });
      else return;
      e.preventDefault(); // a handled shortcut must not also type into the form it opened
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [tab, selected, mode.kind, busy, move, decide]);

  return (
    <div className="queue">
      <div className="queue-toolbar">
        <div className="queue-tabs" role="tablist" aria-label="Queue state">
          <button role="tab" aria-selected={tab === 'pending'} onClick={() => setTab('pending')}>
            Waiting{sorted && tab === 'pending' ? ` (${sorted.length})` : ''}
          </button>
          <button role="tab" aria-selected={tab === 'decided'} onClick={() => setTab('decided')}>
            Decided
          </button>
        </div>
        <label className="queue-sort">
          Sort by{' '}
          <select value={sort} onChange={(e) => setSort(e.target.value as 'age' | 'agent')}>
            <option value="age">age</option>
            <option value="agent">agent</option>
          </select>
        </label>
      </div>

      {notice && <div className="queue-notice" role="alert">{notice}</div>}

      {sorted === null ? (
        <div className="empty">Loading the queue…</div>
      ) : sorted.length === 0 ? (
        <div className="queue-clear">
          {tab === 'pending' ? 'Queue is clear. Nothing is waiting on you.' : 'No decisions recorded yet.'}
        </div>
      ) : (
        <div className="queue-body">
          <div className="queue-list" ref={listRef} role="listbox" aria-label="Approvals">
            {sorted.map((item) => (
              <button
                key={item.id}
                role="option"
                aria-selected={selected?.id === item.id}
                className={`queue-row${selected?.id === item.id ? ' selected' : ''}`}
                onClick={() => setSelectedId(item.id)}
              >
                <AgentChip agent={item.agent} />
                <BrandChip payload={item.payload} />
                <span className="row-summary">{item.summary}</span>
                {item.action_class !== 'standard' && (
                  <span className={`class-chip ${item.action_class}`}>{item.action_class}</span>
                )}
                {/* When it goes out, on the row itself. Reviewing a
                    calendar means comparing sixteen dates, and doing that
                    by opening sixteen items is not reviewing. */}
                {scheduledFor(item) !== null && (
                  <span className="row-when">{whenLabel(scheduledFor(item)!)}</span>
                )}
                <span className="row-age">
                  {tab === 'pending' ? timeAgo(item.requested_at) : (item.decision ?? '')}
                </span>
              </button>
            ))}
          </div>

          {selected && (
            <div className="queue-detail card" aria-live="polite">
              <div className="detail-head">
                <AgentChip agent={selected.agent} />
                <BrandChip payload={selected.payload} />
                <strong>{selected.summary}</strong>
                {selected.action_class !== 'standard' && (
                  <span className={`class-chip ${selected.action_class}`}>{selected.action_class}</span>
                )}
              </div>
              <div className="detail-meta">
                {selected.tool_name} · requested {timeAgo(selected.requested_at)}
                {tab === 'pending' && <> · expires in {expiresIn(selected.expires_at)}</>}
                {scheduledFor(selected) !== null && (
                  <> · goes out {whenLabel(scheduledFor(selected)!)}</>
                )}
              </div>

              {selected.rationale && (
                <div className="detail-rationale">
                  <span className="detail-label">Agent's rationale</span>
                  <p>{selected.rationale}</p>
                </div>
              )}

              <span className="detail-label">Exactly what will be executed</span>
              <Payload payload={selected.payload} />

              {tab === 'decided' && <DecidedMeta item={selected} />}

              {tab === 'pending' && mode.kind === 'view' && (
                <div className="detail-actions">
                  <button className="btn approve" disabled={busy} onClick={() => void decide(selected, { decision: 'approved' })}>
                    Approve <kbd>a</kbd>
                  </button>
                  <button className="btn reject" disabled={busy} onClick={() => setMode({ kind: 'reject', reason: '' })}>
                    Reject <kbd>r</kbd>
                  </button>
                  <button
                    className="btn"
                    disabled={busy}
                    onClick={() => setMode({ kind: 'edit', text: JSON.stringify(selected.payload, null, 2), error: null })}
                  >
                    Edit and approve <kbd>e</kbd>
                  </button>
                  {scheduledFor(selected) !== null && (
                    <button
                      className="btn"
                      disabled={busy}
                      onClick={() => setMode({ kind: 'when', at: scheduledFor(selected)!, confirming: false })}
                    >
                      Change time <kbd>t</kbd>
                    </button>
                  )}
                </div>
              )}

              {/*
                Scheduled or now, chosen at approval (11 September 2026).
                The agent proposes a calendar slot; the person deciding is
                the one who knows whether it should wait for it. This rides
                the existing 'edited' decision rather than inventing a
                second kind of approval - the payload is diffed and stored
                exactly as a hand-edit would be, and the executor re-gates
                it on the way through, so nothing about the audit trail or
                the compliance checks changes.

                Publishing now asks twice. Everything else in this queue is
                recoverable or delayed; this one puts a post on a live
                brand account the moment it is clicked.
              */}
              {mode.kind === 'when' && (
                <div className="detail-form">
                  <span className="detail-label">
                    When should this go out? The agent proposed{' '}
                    {whenLabel(scheduledFor(selected)!)}.
                  </span>
                  <input
                    autoFocus
                    type="datetime-local"
                    className="when-input"
                    value={toLocalInput(mode.at)}
                    onChange={(e) => setMode({ kind: 'when', at: fromLocalInput(e.target.value), confirming: false })}
                  />
                  {mode.confirming ? (
                    <>
                      <div className="queue-notice" role="alert">
                        This publishes to the live account straight away and cannot be taken back.
                      </div>
                      <div className="detail-actions">
                        <button
                          className="btn approve"
                          disabled={busy}
                          onClick={() =>
                            void decide(selected, {
                              decision: 'edited',
                              edited_payload: { ...selected.payload, scheduled_for: new Date().toISOString() },
                            })
                          }
                        >
                          Yes, post it now
                        </button>
                        <button className="btn" onClick={() => setMode({ ...mode, confirming: false })}>
                          Cancel <kbd>esc</kbd>
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="detail-actions">
                      <button
                        className="btn approve"
                        disabled={busy}
                        onClick={() =>
                          void decide(selected, {
                            decision: 'edited',
                            edited_payload: { ...selected.payload, scheduled_for: mode.at },
                          })
                        }
                      >
                        Approve for this time
                      </button>
                      <button className="btn" disabled={busy} onClick={() => setMode({ ...mode, confirming: true })}>
                        Post now instead
                      </button>
                      <button className="btn" onClick={() => setMode({ kind: 'view' })}>
                        Cancel <kbd>esc</kbd>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {mode.kind === 'reject' && (
                <div className="detail-form">
                  <span className="detail-label">Why is this wrong? The reason trains the agent.</span>
                  <textarea
                    autoFocus
                    rows={3}
                    value={mode.reason}
                    placeholder="Required. Be specific; this becomes a labelled example."
                    onChange={(e) => setMode({ kind: 'reject', reason: e.target.value })}
                  />
                  <div className="detail-actions">
                    <button
                      className="btn reject"
                      disabled={busy || mode.reason.trim().length < 5}
                      onClick={() => void decide(selected, { decision: 'rejected', rejection_reason: mode.reason.trim() })}
                    >
                      Reject with reason
                    </button>
                    <button className="btn" onClick={() => setMode({ kind: 'view' })}>
                      Cancel <kbd>esc</kbd>
                    </button>
                  </div>
                </div>
              )}

              {mode.kind === 'edit' && (
                <div className="detail-form">
                  <span className="detail-label">Edit the payload, then approve. The diff is stored.</span>
                  <textarea
                    autoFocus
                    rows={10}
                    className="mono"
                    value={mode.text}
                    onChange={(e) => setMode({ kind: 'edit', text: e.target.value, error: null })}
                  />
                  {mode.error && <div className="queue-notice">{mode.error}</div>}
                  <div className="detail-actions">
                    <button
                      className="btn approve"
                      disabled={busy}
                      onClick={() => {
                        try {
                          const edited = JSON.parse(mode.text) as Record<string, unknown>;
                          void decide(selected, { decision: 'edited', edited_payload: edited });
                        } catch {
                          setMode({ ...mode, error: 'That is not valid JSON yet.' });
                        }
                      }}
                    >
                      Approve edited version
                    </button>
                    <button className="btn" onClick={() => setMode({ kind: 'view' })}>
                      Cancel <kbd>esc</kbd>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'pending' && (sorted?.length ?? 0) > 0 && (
        <div className="kbd-legend">
          <kbd>j</kbd>/<kbd>k</kbd> move · <kbd>a</kbd> approve · <kbd>r</kbd> reject · <kbd>e</kbd> edit
          {me.role === 'operator' ? ' · showing your agents only' : ''}
        </div>
      )}
    </div>
  );
}

function expiresIn(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.floor(ms / 3_600_000);
  return hours >= 1 ? `${hours}h` : `${Math.max(1, Math.floor(ms / 60_000))}m`;
}

/**
 * What each Afrofiliate CTA audience means, in the reviewer's terms. The
 * lines themselves live in brand/voice/afrofiliate.md and are rendered by
 * the Media Machine; this only has to say enough for someone to decide
 * whether the closing slide is pointed at the right people.
 */
const CTA_AUDIENCE_HINT: Record<string, string> = {
  creator: 'closes to creators',
  brand: 'closes to Black-owned brands',
  dual: 'closes to brands and creators together',
};

/**
 * Render the payload readably (§8): a draft with a body reads as the
 * message it is; everything else is labelled fields, never raw JSON.
 */
function Payload({ payload }: { payload: Record<string, unknown> }) {
  // preview_url and frame_urls leave the field list: they render as the
  // asset below (or labelled placeholders), not as strings a reviewer has
  // to squint at.
  const {
    body,
    rationale: _dup,
    preview_url: rawPreview,
    frame_urls: rawFrames,
    cta_audience: rawAudience,
    ...rest
  } = payload;

  /**
   * Long prose is pulled out of the field grid and rendered as a wrapped
   * block. It has to be: the grid's first column is sized `max-content`,
   * which by definition never wraps, so a full post caption stretched the
   * panel past the right edge and the copy under review could not be
   * read at all. Reviewing what you cannot see is not reviewing.
   *
   * Keyed on the field names that carry writing rather than on length, so
   * a short caption and a long one render the same way and the panel does
   * not change shape as you move down the queue.
   */
  const PROSE_FIELDS = ['copy', 'reply', 'caption', 'text', 'message', 'subject'];
  const prose: [string, string][] = [];
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rest)) {
    if (PROSE_FIELDS.includes(key) && typeof value === 'string') prose.push([key, value]);
    else fields[key] = value;
  }
  const previewUrl = typeof rawPreview === 'string' ? rawPreview : null;
  const frameUrls = Array.isArray(rawFrames) ? rawFrames.filter((u): u is string => typeof u === 'string') : [];
  // The CTA audience is the one payload field a reviewer is expected to
  // CHANGE rather than just read, so it gets its own row saying what it
  // does and what happens if you edit it. Editing it and approving
  // re-renders the closing slide before the post goes out (3 September
  // 2026); leaving it alone publishes the frames shown below.
  const ctaAudience = typeof rawAudience === 'string' ? rawAudience : null;
  return (
    <div className="payload">
      {Object.keys(fields).length > 0 && (
        <dl>
          {Object.entries(fields).map(([key, value]) => (
            <div key={key} className="payload-field">
              <dt>{key.replaceAll('_', ' ')}</dt>
              <dd>{formatValue(key, value)}</dd>
            </div>
          ))}
        </dl>
      )}
      {ctaAudience && (
        <div className="payload-field payload-cta-audience">
          <dt>CTA audience</dt>
          <dd>
            <strong>{ctaAudience}</strong>
            <span className="payload-hint">{CTA_AUDIENCE_HINT[ctaAudience] ?? 'closing slide audience'}</span>
            <span className="payload-hint">
              Edit and approve to change it: the closing slide re-renders with that line before the post goes out.
            </span>
          </dd>
        </div>
      )}
      {prose.map(([key, value]) => (
        <div key={key} className="payload-prose">
          <span className="detail-label">{key.replaceAll('_', ' ')}</span>
          <div className="payload-letter">{value}</div>
        </div>
      ))}
      {frameUrls.length > 0 ? (
        <div className="asset-frames">
          {frameUrls.map((url, i) => (
            <figure key={i} className="asset-frame">
              <AssetPreview url={url} />
              <figcaption>
                {i === frameUrls.length - 1
                  ? `Slide ${i + 1}, CTA (appended by the renderer${ctaAudience ? `, ${ctaAudience}` : ''})`
                  : `Slide ${i + 1}`}
              </figcaption>
            </figure>
          ))}
        </div>
      ) : (
        previewUrl && <AssetPreview url={previewUrl} />
      )}
      {typeof body === 'string' && <div className="payload-letter">{body}</div>}
    </div>
  );
}

/**
 * §4.7: a human approving a carousel sees the carousel, never a JSON
 * payload, and sees EVERY slide: approving the cover and letting the rest
 * go out unseen is not review. Only an https URL is embedded; a shadow://
 * placeholder from a shadow-mode render is labelled for what it is rather
 * than fetched, and a URL that fails to load degrades to the same label so
 * a broken render never silently looks like "no asset".
 *
 * The failure message names both causes and asserts neither, which it did
 * not on 3 September 2026: it said the URLs had expired, because presigned
 * frame URLs do expire after an hour and that was the expected failure.
 * The real cause was this page's own Content-Security-Policy refusing the
 * S3 host, and the confident wording cost an evening. A browser gives an
 * <img> onError handler no way to tell a blocked request from a dead one,
 * so the honest thing is to say so and let the reader check both. Either
 * way the render itself is safe in S3.
 */
function AssetPreview({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);
  const isHttps = url.startsWith('https://');
  if (!isHttps || failed) {
    return (
      <div className="asset-preview asset-preview-placeholder">
        {isHttps ? (
          <>
            Asset preview did not load. Links are minted fresh each time the queue loads
            (7 September 2026), so expiry means a very stale tab: refresh first. If it
            still fails, the page may have blocked the host - open it in a new tab to tell.
          </>
        ) : (
          'Shadow render, no previewable asset'
        )}
        <span className="asset-preview-url">{url}</span>
      </div>
    );
  }
  return (
    <a className="asset-preview" href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="Rendered asset preview" onError={() => setFailed(true)} />
    </a>
  );
}

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (key === 'confidence' && typeof value === 'number') return `${Math.round(value * 100)}%`;
  if (/_minor$/.test(key) && typeof value === 'number') return (value / 100).toFixed(2);
  if (typeof value === 'object') return JSON.stringify(value, null, 1);
  return String(value);
}

function DecidedMeta({ item }: { item: ApprovalItem }) {
  const scheduled = scheduledFor(item);
  return (
    <div className="decided-meta">
      <span className="detail-label">Decision</span>
      <p>
        <strong>{item.decision}</strong> by {item.decided_by} · {item.decided_at ? timeAgo(item.decided_at) : ''}
        {item.execution_status && <> · execution: {item.execution_status}</>}
        {/* "executed" on a schedule_post means the schedule was accepted,
            not that anything published. Saying only "executed" reads as
            "it is out" and sends someone to Instagram to look for a post
            that is not due for days. */}
        {scheduled !== null && (
          <>
            {' '}
            ·{' '}
            <strong>
              {Date.parse(scheduled) > Date.now() ? 'goes out' : 'sent'} {whenLabel(scheduled)}
            </strong>
          </>
        )}
        {item.execution_error && (
          <div className="execution-error" role="note">
            {item.execution_error}
          </div>
        )}
      </p>
      {item.rejection_reason && <p className="rejection">“{item.rejection_reason}”</p>}
      {item.diff && <pre className="diff">{item.diff}</pre>}
    </div>
  );
}
